import React, { useEffect, useRef, useState, useCallback } from "react";
import { Network } from "vis-network/standalone";
import "vis-network/styles/vis-network.css";
import { Box, Typography } from "@mui/material";

const NetworkPreview = ({ nodes, edges, isEditable = false, onNodeMove }) => {
  const networkContainer = useRef(null);
  const networkInstance = useRef(null);
  const [containerSize, setContainerSize] = useState({
    width: 800,
    height: 600,
  });

  const updateContainerSize = useCallback(() => {
    if (networkContainer.current) {
      setContainerSize({
        width: networkContainer.current.offsetWidth,
        height: networkContainer.current.offsetHeight,
      });
    }
  }, []);

  useEffect(() => {
    updateContainerSize();
    window.addEventListener("resize", updateContainerSize);
    return () => window.removeEventListener("resize", updateContainerSize);
  }, [updateContainerSize]);

  useEffect(() => {
    if (!networkContainer.current || nodes.length === 0) return;

    const { width: containerWidth, height: containerHeight } = containerSize;

    // Find min/max column & row
    const columnValues = nodes.map((n) => n.column);
    const rowValues = nodes.map((n) => n.row);

    const minColumn = Math.min(...columnValues);
    const maxColumn = Math.max(...columnValues);
    const minRow = Math.min(...rowValues);
    const maxRow = Math.max(...rowValues);

    // Padding
    const horizontalPadding = containerWidth * 0.05;
    const verticalPadding = containerHeight * 0.05;

    // Dynamic spacing
    const columnSpacing =
      maxColumn !== minColumn
        ? (containerWidth - horizontalPadding * 2) / (maxColumn - minColumn)
        : containerWidth / 2;

    const rowSpacing =
      maxRow !== minRow
        ? (containerHeight - verticalPadding * 2) / (maxRow - minRow)
        : containerHeight / 2;

    // Base node size
    const baseNodeSize = Math.min(30, Math.max(10, containerWidth / 50));
    const nodeWidth = baseNodeSize * 3;
    const nodeHeight = baseNodeSize * 3;
    const arrowScaleFactor = Math.min(2, Math.max(0.5, containerWidth / 1000));

    // Group nodes by (column,row)
    const nodeGroups = {};
    nodes.forEach((node) => {
      const key = `${node.column}-${node.row}`;
      if (!nodeGroups[key]) nodeGroups[key] = [];
      nodeGroups[key].push(node);
    });

    // Calculate positions
    const visNodes = [];
    Object.keys(nodeGroups).forEach((key) => {
      const groupNodes = nodeGroups[key];
      const [col, row] = key.split("-").map(Number);

      const verticalSpacing =
        groupNodes.length > 1
          ? Math.min(nodeHeight * 1.5, rowSpacing / groupNodes.length)
          : 0;
      const horizontalSpacing =
        groupNodes.length > 1
          ? Math.min(nodeWidth * 1.5, columnSpacing / groupNodes.length)
          : 0;

      const totalVertical = (groupNodes.length - 1) * verticalSpacing;
      const totalHorizontal = (groupNodes.length - 1) * horizontalSpacing;

      groupNodes.forEach((node, idx) => {
        const x =
          (col - minColumn) * columnSpacing +
          idx * horizontalSpacing -
          totalHorizontal / 2;
        const y =
          (row - minRow) * rowSpacing +
          idx * verticalSpacing -
          totalVertical / 2;

        visNodes.push({
          id: node.id,
          label: node.label,
          shape: node.shape || "box",
          size: baseNodeSize,
          color: {
            background: node.color,
            highlight: { background: node.color },
          },
          font: { size: baseNodeSize / 2, color: "#fff", bold: true },
          margin: 10,
          x,
          y,
          fixed: true,
          widthConstraint: { minimum: nodeWidth, maximum: nodeWidth },
          heightConstraint: { minimum: nodeHeight, maximum: nodeHeight },
        });
      });
    });

    // Calculate center offset
    const allX = visNodes.map((n) => n.x);
    const allY = visNodes.map((n) => n.y);
    const centerX = (Math.min(...allX) + Math.max(...allX)) / 2;
    const centerY = (Math.min(...allY) + Math.max(...allY)) / 2;
    const offsetX = containerWidth / 2 - centerX;
    const offsetY = containerHeight / 2 - centerY;

    // Apply center offset
    visNodes.forEach((n) => {
      n.x += offsetX;
      n.y += offsetY;
    });

    // Helper function to check if two line segments intersect
    const doLinesIntersect = (line1, line2) => {
      const { x1, y1, x2, y2 } = line1;
      const { x1: x3, y1: y3, x2: x4, y2: y4 } = line2;

      const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
      if (denom === 0) return false; // Lines are parallel

      const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
      const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;

      return t >= 0 && t <= 1 && u >= 0 && u <= 1;
    };

    // Helper function to check if a line passes through a node
    const doesLinePassThroughNode = (line, node, nodeSize) => {
      const { x1, y1, x2, y2 } = line;
      const { x: nodeX, y: nodeY } = node;

      // Calculate distance from point to line
      const A = y2 - y1;
      const B = x1 - x2;
      const C = x2 * y1 - x1 * y2;

      const distance =
        Math.abs(A * nodeX + B * nodeY + C) / Math.sqrt(A * A + B * B);

      // Check if the point is within the line segment bounds
      const minX = Math.min(x1, x2) - nodeSize;
      const maxX = Math.max(x1, x2) + nodeSize;
      const minY = Math.min(y1, y2) - nodeSize;
      const maxY = Math.max(y1, y2) + nodeSize;

      const isWithinBounds =
        nodeX >= minX && nodeX <= maxX && nodeY >= minY && nodeY <= maxY;

      return distance < nodeSize && isWithinBounds;
    };

    // Build edges with collision detection
    const visEdges = edges
      .filter((edge) => edge.from && edge.to)
      .map((edge, index) => {
        const fromNode = visNodes.find((n) => n.id === edge.from);
        const toNode = visNodes.find((n) => n.id === edge.to);

        if (!fromNode || !toNode) return null;

        let smoothType = "dynamic";
        let roundness = 0.5;

        // Create line representation for current edge
        const currentLine = {
          x1: fromNode.x,
          y1: fromNode.y,
          x2: toNode.x,
          y2: toNode.y,
        };

        // Check for intersections with other edges
        const otherEdges = edges.slice(0, index);
        let hasIntersection = false;

        for (const otherEdge of otherEdges) {
          const otherFromNode = visNodes.find((n) => n.id === otherEdge.from);
          const otherToNode = visNodes.find((n) => n.id === otherEdge.to);

          if (otherFromNode && otherToNode) {
            const otherLine = {
              x1: otherFromNode.x,
              y1: otherFromNode.y,
              x2: otherToNode.x,
              y2: otherToNode.y,
            };

            if (doLinesIntersect(currentLine, otherLine)) {
              hasIntersection = true;
              break;
            }
          }
        }

        // Check if edge passes through any intermediate nodes
        let passesThroughNode = false;
        for (const node of visNodes) {
          if (node.id !== edge.from && node.id !== edge.to) {
            if (doesLinePassThroughNode(currentLine, node, nodeWidth / 2)) {
              passesThroughNode = true;
              break;
            }
          }
        }

        // Apply perpendicular routing if there's a collision
        if (hasIntersection || passesThroughNode) {
          smoothType = "diagonalCross";
          roundness = 0.4;

          // Alternate between clockwise and counterclockwise for variety
          if (index % 2 === 1) {
            smoothType = "dynamic";
          }
        }

        return {
          id: edge.id,
          from: edge.from,
          to: edge.to,
          label: edge.label || "",
          arrows: {
            to: { enabled: true, type: "arrow", scaleFactor: arrowScaleFactor },
          },
          smooth: {
            type: smoothType,
            forceDirection: smoothType === "dynamic" ? "horizontal" : "none",
            roundness: roundness,
          },
          color: {
            color: "#4672c3",
            highlight: "#4672c3",
          },
        };
      })
      .filter((edge) => edge !== null);

    const options = {
      nodes: {
        scaling: { min: 10, max: 30, label: { enabled: false } },
        shapeProperties: { interpolation: false },
      },
      edges: {
        width: 2,
        smooth: {
          enabled: true,
          type: "dynamic",
          roundness: 0.5,
          forceDirection: "none",
        },
        selectionWidth: 3,
        hoverWidth: 3,
      },
      physics: { enabled: false },
      interaction: {
        dragNodes: false,
        dragView: false,
        zoomView: false,
        hover: true,
        selectConnectedEdges: false,
      },
      manipulation: { enabled: false },
    };

    if (networkInstance.current) networkInstance.current.destroy();
    networkInstance.current = new Network(
      networkContainer.current,
      { nodes: visNodes, edges: visEdges },
      options
    );

    // Editable drag
    if (isEditable && onNodeMove) {
      networkInstance.current.on("dragEnd", (params) => {
        if (params.nodes.length > 0) {
          const nodeId = params.nodes[0];
          const position = networkInstance.current.getPositions([nodeId])[
            nodeId
          ];
          const column = Math.round(position.x / columnSpacing + minColumn);
          const row = Math.round(position.y / rowSpacing + minRow);
          onNodeMove(nodeId, column, row);
        }
      });
    }
  }, [nodes, edges, containerSize, isEditable, onNodeMove]);

  return (
    <Box sx={{ height: "100%", minHeight: 800, position: "relative" }}>
      {nodes.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            minHeight: 500,
          }}
        >
          <Typography color="textSecondary">
            Add nodes and edges to see the network preview
          </Typography>
        </Box>
      ) : (
        <div ref={networkContainer} style={{ width: "100%", height: "65vh" }} />
      )}
    </Box>
  );
};

export default NetworkPreview;
